import { useCallback } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * Custom hook that wraps react-i18next's useTranslation
 * Provides additional utilities and consistent error handling
 */
export const useTranslation = (namespace = 'translation') => {
  const { t, i18n, ready } = useI18nTranslation(namespace);

  // Stable reference so useEffect(..., [t]) does not re-run every render
  const translate = useCallback((key, options = {}) => {
    try {
      const result = t(key, options);
      if (result === key && process.env.NODE_ENV === 'development') {
        // console.warn(`Translation key "${key}" not found for language "${i18n.language}"`);
      }
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // console.warn(`Translation key "${key}" not found:`, error);
      }
      return key; // Return the key as fallback
    }
  }, [t]);

  // Get current language
  const currentLanguage = i18n.language;

  // Check if language is Swedish
  const isSwedish = currentLanguage === 'sv';

  // Check if language is English
  const isEnglish = currentLanguage === 'en';

  // Change language with error handling
  const changeLanguage = async (language) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        // console.log(`Attempting to change language from ${i18n.language} to ${language}`);
      }
      await i18n.changeLanguage(language);
      if (process.env.NODE_ENV === 'development') {
        // console.log(`Language successfully changed to: ${i18n.language}`);
      }
    } catch (error) {
      // console.error('Failed to change language:', error);
    }
  };

  return {
    t: translate,
    i18n,
    ready,
    currentLanguage,
    isSwedish,
    isEnglish,
    changeLanguage
  };
};

export default useTranslation;